import React from 'react'

export interface AvatarProps {
  url?: string
  name?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
  src?: string | null
  fallback?: string
}

const sizeMap: Record<string, number> = {
  sm: 32,
  md: 48,
  lg: 64,
}

const textSizeMap: Record<string, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
}

function getInitials(name?: string): string {
  if (!name) return ''
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ url, name, size = 'md', className = '', src, fallback, ...rest }, ref) => {
    const effectiveUrl = url || src || undefined
    const effectiveName = name || fallback
    const pixelSize = sizeMap[size]
    const textClass = textSizeMap[size]

    const containerStyle: React.CSSProperties = {
      width: pixelSize,
      height: pixelSize,
    }

    return (
      <div
        ref={ref}
        className={[
          'shrink-0 rounded-full overflow-hidden',
          url ? '' : `bg-green/10 text-green font-display font-medium flex items-center justify-center ${textClass}`,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={containerStyle}
        title={effectiveName}
        {...rest}
      >
        {effectiveUrl ? (
          <img
            src={effectiveUrl}
            alt={effectiveName || 'Avatar'}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <span>{getInitials(effectiveName)}</span>
        )}
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'

export default Avatar
